import { useEffect, useState } from 'react';
import { lineTotal, poSubtotal, poTotalJmd } from '../../lib/purchaseOrders';
import { formatJMD, formatUSD } from '../../lib/finances';
import VendorAutocomplete from '../VendorAutocomplete';
import { todayISO } from '../../lib/dates';


const BLANK_ITEM = { item_name: '', quantity: '', unit_cost: '', notes: '' };

const BLANK = {
  vendorName: '',
  orderDate: todayISO(),
  expectedDeliveryDate: '',
  currency: 'JMD',
  exchangeRate: '',
  notes: '',
};

// submitLabel defaults off initialPO rather than being required: the form
// already knows whether it's adding or updating, and a caller that forgot to
// pass it rendered a button with no text at all.
export default function PurchaseOrderForm({
  initialPO,
  prefill,
  reorderItems = [],
  vendors,
  onCreateVendor,
  submitLabel = initialPO ? 'Update order' : 'Create order',
  onSubmit,
  onCancel,
}) {
  const [vendorName, setVendorName] = useState(BLANK.vendorName);
  const [orderDate, setOrderDate] = useState(BLANK.orderDate);
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState(BLANK.expectedDeliveryDate);
  const [currency, setCurrency] = useState(BLANK.currency);
  const [exchangeRate, setExchangeRate] = useState(BLANK.exchangeRate);
  const [notes, setNotes] = useState(BLANK.notes);
  const [items, setItems] = useState([{ ...BLANK_ITEM }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Reorder items pulled into this order, so they can be stamped as Ordered
  // once it saves. Seeded from `prefill` when you arrived from the To Order
  // tab, and added to by the picker below.
  const [linkedIds, setLinkedIds] = useState([]);

  useEffect(() => {
    if (initialPO) {
      setLinkedIds([]);
      setVendorName(initialPO.vendor?.name || '');
      setOrderDate(initialPO.order_date);
      setExpectedDeliveryDate(initialPO.expected_delivery_date || '');
      setCurrency(initialPO.currency);
      setExchangeRate(initialPO.currency === 'USD' ? String(initialPO.exchange_rate) : '');
      setNotes(initialPO.notes || '');
      setItems(
        (initialPO.purchase_order_items || []).map((i) => ({
          item_name: i.item_name,
          quantity: String(i.quantity),
          unit_cost: String(i.unit_cost),
          notes: i.notes || '',
        }))
      );
    } else {
      // A new order raised from the To Order list arrives pre-filled with the
      // items and vendor already noted there. Prices are deliberately left
      // blank — the reorder list never claimed to know them.
      setVendorName(prefill?.vendorName || BLANK.vendorName);
      setOrderDate(BLANK.orderDate);
      setExpectedDeliveryDate(BLANK.expectedDeliveryDate);
      setCurrency(BLANK.currency);
      setExchangeRate(BLANK.exchangeRate);
      setNotes(BLANK.notes);
      setItems(
        prefill?.items?.length
          ? prefill.items.map((i) => ({ ...BLANK_ITEM, ...i, quantity: String(i.quantity ?? '') }))
          : [{ ...BLANK_ITEM }]
      );
      setLinkedIds(prefill?.itemIds || []);
    }
    setError(null);
  }, [initialPO, prefill]);

  const updateItem = (index, field, value) => {
    setItems((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addItemRow = () => setItems((prev) => [...prev, { ...BLANK_ITEM }]);

  const removeItemRow = (index) => setItems((prev) => prev.filter((_, i) => i !== index));

  // A purchase order goes to one vendor, so only that vendor's outstanding
  // items are offered — plus items with no vendor set, which can go on any
  // order. Anything already pulled in disappears from the list.
  const selectedVendor = vendors.find((v) => v.name === vendorName);
  const availableToOrder = reorderItems.filter((item) => {
    if (item.status !== 'Needed') return false;
    if (linkedIds.includes(item.id)) return false;
    if (!selectedVendor) return true;
    return !item.vendor_id || item.vendor_id === selectedVendor.id;
  });

  const addFromReorder = (item) => {
    setItems((prev) => {
      // Replace a single untouched blank row rather than leaving it behind.
      const base = prev.length === 1 && !prev[0].item_name.trim() ? [] : prev;
      return [
        ...base,
        {
          ...BLANK_ITEM,
          item_name: item.item_name,
          // Free-text quantity ("2 kg") can't go in a numeric column, so it
          // rides along in the line note where it stays readable.
          notes: [item.quantity, item.notes].filter(Boolean).join(' · '),
        },
      ];
    });
    setLinkedIds((prev) => [...prev, item.id]);
    // Picking an item from a vendor you haven't chosen yet fills it in.
    if (!vendorName && item.vendor?.name) setVendorName(item.vendor.name);
  };

  const numericRate = currency === 'USD' ? Number(exchangeRate || 0) : 1;
  const subtotal = poSubtotal(items.map((i) => ({ quantity: i.quantity, unit_cost: i.unit_cost })));
  const totalJmd = poTotalJmd(items.map((i) => ({ quantity: i.quantity, unit_cost: i.unit_cost })), numericRate);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const vendor = vendors.find((v) => v.name === vendorName);
    if (!vendor) {
      setError('Pick a vendor from the list, or add it as new.');
      return;
    }

    if (currency === 'USD' && numericRate <= 0) {
      setError('Enter the exchange rate for this order.');
      return;
    }

    const cleanItems = items.filter((i) => i.item_name.trim());
    if (cleanItems.length === 0) {
      setError('Add at least one line item.');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(
        {
          vendor_id: vendor.id,
          order_date: orderDate,
          expected_delivery_date: expectedDeliveryDate || null,
          currency,
          exchange_rate: numericRate,
          notes: notes || null,
        },
        cleanItems.map((i) => ({
          item_name: i.item_name,
          quantity: Number(i.quantity || 0),
          unit_cost: Number(i.unit_cost || 0),
          notes: i.notes || null,
        })),
        linkedIds
      );
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="po-form" onSubmit={handleSubmit}>
      <header className="po-form-head">
        <h3>{initialPO ? `Edit ${initialPO.po_number}` : 'New purchase order'}</h3>
        <p>Pick the vendor, then list what you're buying and what it costs.</p>
      </header>

      <div className="po-form-grid">
        <label>
          <span className="po-field-label">Vendor</span>
          <VendorAutocomplete
            value={vendorName}
            onChange={setVendorName}
            vendors={vendors}
            onCreateVendor={onCreateVendor}
          />
        </label>

        <label>
          <span className="po-field-label">Order date</span>
          <input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} required />
        </label>

        <label>
          <span className="po-field-label">
            Expected delivery <span className="po-field-hint">optional</span>
          </span>
          <input
            type="date"
            value={expectedDeliveryDate}
            onChange={(e) => setExpectedDeliveryDate(e.target.value)}
          />
        </label>

        <label>
          <span className="po-field-label">Currency</span>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <option value="JMD">JMD</option>
            <option value="USD">USD</option>
          </select>
        </label>

        {currency === 'USD' && (
          <label>
            <span className="po-field-label">Exchange rate (JMD per USD)</span>
            <input
              type="number"
              min="0"
              step="0.0001"
              value={exchangeRate}
              onChange={(e) => setExchangeRate(e.target.value)}
              required
            />
          </label>
        )}

        <label className="po-form-wide">
          <span className="po-field-label">
            Notes <span className="po-field-hint">optional</span>
          </span>
          <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>

      <h3 className="po-form-subheading">Line items</h3>

      <div className="po-item-rows">
        <div className="po-item-row po-item-row-header">
          <span>Item</span>
          <span className="po-col-num">Qty</span>
          <span className="po-col-num">Unit cost ({currency})</span>
          <span className="po-col-num">Line total</span>
          <span className="po-col-actions" />
        </div>
        {items.map((item, index) => (
          <div className="po-item-row" key={index}>
            <input
              type="text"
              value={item.item_name}
              onChange={(e) => updateItem(index, 'item_name', e.target.value)}
              placeholder="e.g. Shea Butter 25kg"
            />
            <input
              className="po-num"
              type="number"
              min="0"
              step="0.01"
              value={item.quantity}
              onChange={(e) => updateItem(index, 'quantity', e.target.value)}
            />
            <input
              className="po-num"
              type="number"
              min="0"
              step="0.01"
              value={item.unit_cost}
              onChange={(e) => updateItem(index, 'unit_cost', e.target.value)}
            />
            <span className="po-item-line-total">
              {currency === 'USD' ? formatUSD(lineTotal(item)) : formatJMD(lineTotal(item))}
            </span>
            <button
              type="button"
              className="po-item-remove"
              onClick={() => removeItemRow(index)}
              disabled={items.length === 1}
              aria-label="Remove this line"
              title="Remove this line"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <div className="po-item-adders">
        <button type="button" className="po-add-row" onClick={addItemRow}>
          + Add item
        </button>
      </div>

      {/* The whole point of the To Order list is that you don't retype what
          you already wrote down. Starting a purchase order from scratch used
          to give you no way back to it. */}
      {availableToOrder.length > 0 && (
        <section className="po-from-toorder">
          <h4>
            On your To Order list
            <span className="po-from-toorder-count">{availableToOrder.length}</span>
          </h4>
          <p className="po-from-toorder-hint">
            {selectedVendor
              ? `Outstanding items for ${selectedVendor.name}. Click to add them to this order.`
              : 'Click an item to add it — its vendor fills in automatically.'}
          </p>
          <ul>
            {availableToOrder.map((item) => (
              <li key={item.id}>
                <button type="button" onClick={() => addFromReorder(item)}>
                  <span className="po-toorder-name">{item.item_name}</span>
                  <span className="po-toorder-meta">
                    {item.quantity && <span>{item.quantity}</span>}
                    {!selectedVendor && item.vendor?.name && <span>{item.vendor.name}</span>}
                  </span>
                  <span className="po-toorder-add" aria-hidden="true">+</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="po-form-totals">
        <div className="po-total-row">
          <span>Subtotal</span>
          <strong>{currency === 'USD' ? formatUSD(subtotal) : formatJMD(subtotal)}</strong>
        </div>
        {currency === 'USD' && (
          <div className="po-total-row po-total-converted">
            <span>In JMD</span>
            <strong>{formatJMD(totalJmd)}</strong>
          </div>
        )}
      </div>

      {error && <p className="po-form-error">{error}</p>}

      <div className="po-form-actions">
        <button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : submitLabel}
        </button>
        <button type="button" className="po-form-cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
